import React from 'react';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext, useState } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import EmailTextField from 'shared/lib/components/controls/textFields/emailTextField';
import PasswordTextField from 'shared/lib/components/controls/textFields/passwordTextField';
import { useTranslation } from 'react-i18next';
import mobxStore from 'app/store/rootStore';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import { SSO_URL } from 'app/api/api';
import { useSsoAuth } from 'app/hooks/useSsoAuth';
import IconIcPass from 'shared/icons/iconIcPass';

export default observer(function LoginForm({
  onRestoreClick,
  footer,
}: {
  onRestoreClick: () => void;
  footer: React.ReactNode;
}) {
  const { t } = useTranslation();
  const {
    mobxStore: { authStore },
  } = useContext(MobxContext);
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  useSsoAuth();

  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object({
    email: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .email(t('errors.validationSchema.email')),
    password: yup.string().required(t('errors.validationSchema.required')),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async ({ email, password }) => {
      try {
        setIsLoading(true);
        await authStore.login({ username: email, password });
        setIsLoading(false);
      } catch (e) {
        mobxStore.notificationsStore.errorMessage = t(
          'notifications.errors.default'
        );
        console.error(e);
        setIsLoading(false);
      }
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  return (
    <>
      <form
        id="loginForm"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(sp.s60),
        }}
        onSubmit={formik.handleSubmit}
      >
        <EmailTextField
          label={t('login.email')}
          id="email"
          name="email"
          placeholder={t('login.emailPlaceholder')}
          value={formik.values.email}
          onChange={handleOnChange}
          onBlur={formik.handleBlur}
          error={isFormikFieldError('email') || !!authStore.errors.signin}
          helperText={
            getFormikFieldErrorText('email') ||
            (authStore.errors.signin && t(`errors.${authStore.errors.signin}`))
          }
          autoFocus={true}
          labelSxProps={{
            paddingLeft: '12px',
            color: theme.palette.custom.customDark,
          }}
          sx={{
            '& .MuiInputBase-root': { color: theme.palette.custom.customDark },
          }}
        ></EmailTextField>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp.s20 }}>
          <PasswordTextField
            label={t('login.password')}
            id="password"
            name="password"
            placeholder={t('login.passwordPlaceholder')}
            value={formik.values.password}
            onChange={handleOnChange}
            onBlur={formik.handleBlur}
            error={isFormikFieldError('password') || !!authStore.errors.signin}
            helperText={
              getFormikFieldErrorText('password') ||
              (authStore.errors.signin &&
                t(`errors.${authStore.errors.signin}`))
            }
            labelSxProps={{
              paddingLeft: '12px',
              color: theme.palette.custom.customDark,
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.palette.custom.customDark,
              },
            }}
          ></PasswordTextField>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: '12px',
            }}
          >
            <Typography
              variant="f14"
              color="primary.main"
              sx={{ cursor: 'pointer' }}
              onClick={onRestoreClick}
            >
              {t('login.isForgetPassword')}
            </Typography>
          </Box>
        </Box>
      </form>
      <PrimaryButton
        sx={{
          width: 1,
          borderRadius: '16px',
        }}
        form="loginForm"
        type="submit"
        loading={isLoading}
        onClick={() => setFormikSubmited(true)}
      >
        <Typography variant="f16">{t('login.comeIn')}</Typography>
      </PrimaryButton>

      {footer}

      <Divider sx={{ width: 1, color: theme.palette.custom.lightGrey }}>
        или
      </Divider>

      <SecondaryButton
        sx={{
          'width': 1,
          'borderRadius': '16px',
          '& .MuiTypography-root': { fontSize: 16 },
        }}
        color="inherit"
        variant="contained"
        href={SSO_URL}
        startIcon={<IconIcPass></IconIcPass>}
      >
        <Typography variant="f20">{t('login.loginSSO')}</Typography>
      </SecondaryButton>
    </>
  );
});
